<template>
  <fieldset class="custom-fieldset">
    <legend>
      {{ $t("report.chosenDelegations") }}
      <v-tooltip bottom>
        {{ $t("report.onlySettledDelegationsTooltip") }}
        <template #activator="{on}">
          <v-icon class="ml-2" v-on="on" small>info</v-icon>
        </template>
      </v-tooltip>
    </legend>
    <v-row style="margin-bottom: 15px">
      <v-col class="px-2" cols="4">
        <v-text-field :label="$t('global.search')" append-icon="search" hide-details single-line v-model="criteria.search" />
      </v-col>
      <v-col class="px-2" md="2" cols="12">
        <v-select :label="$t('global.dateSpanType')" :items="dateSpanTypes" v-model="criteria.date_span_type" />
      </v-col>
      <v-col class="px-2" md="2" cols="12" v-if="criteria.date_span_type === dateSpanTypesConst.DATE_SPAN_TYPE_MONTHLY">
        <v-select :label="$t('global.month')" :items="availableMonths" v-model="criteria.month" />
      </v-col>
      <v-col class="px-2" md="2" cols="12" v-if="criteria.date_span_type === dateSpanTypesConst.DATE_SPAN_TYPE_QUARTERLY">
        <v-select :label="$t('global.quarter')" :items="availableQuarters" v-model="criteria.quarter" />
      </v-col>
      <v-col
        class="px-2"
        :md2="criteria.date_span_type !== dateSpanTypesConst.DATE_SPAN_TYPE_YEARLY"
        :md4="criteria.date_span_type === dateSpanTypesConst.DATE_SPAN_TYPE_YEARLY"
        cols="12"
        v-if="
          [dateSpanTypesConst.DATE_SPAN_TYPE_MONTHLY, dateSpanTypesConst.DATE_SPAN_TYPE_QUARTERLY, dateSpanTypesConst.DATE_SPAN_TYPE_YEARLY].includes(
            criteria.date_span_type
          )
        "
      >
        <v-select :label="$t('global.year')" :items="availableYears" v-model="criteria.year" />
      </v-col>
      <v-col class="px-2" md="2" cols="12" v-if="criteria.date_span_type === 1">
        <date-picker :clearable="true" :label="$t('report.delegationReportForm.fromDate')" :model.sync="criteria.from_date" :placeholder="$t('global.all')" />
      </v-col>
      <v-col class="px-2" md="2" cols="12" v-if="criteria.date_span_type === 1">
        <date-picker :clearable="true" :label="$t('report.delegationReportForm.toDate')" :model.sync="criteria.to_date" :placeholder="$t('global.all')" />
      </v-col>
      <v-col class="px-2" md="2" cols="12">
        <v-select :items="dateTypeNames" :label="$t('report.delegationReportForm.dateType')" v-model="criteria.date_type" />
      </v-col>
      <v-col class="px-2" md="2" cols="12">
        <v-autocomplete
          :items="users"
          :label="$t('delegation.form.fields.delegated.name')"
          item-text="full_name"
          item-value="id"
          v-model="criteria.user_ids"
          clearable
          multiple
          :placeholder="$t('global.all')"
        />
      </v-col>
      <v-col class="px-2" md="2" cols="12">
        <v-autocomplete
          :items="costCentres"
          :label="$t('global.costCentre')"
          v-model="criteria.cost_centre_ids"
          clearable
          multiple
          item-text="name"
          item-value="id"
          :placeholder="$t('global.all')"
        />
      </v-col>
      <v-col class="px-2" md="2" cols="12">
        <v-autocomplete
          :items="organizationStructures"
          :label="$t('global.organizationStructure')"
          v-model="criteria.organization_structure_ids"
          clearable
          multiple
          item-text="name"
          item-value="id"
          :placeholder="$t('global.all')"
        />
      </v-col>
      <v-col class="px-2" v-if="isReInvoicingForExternalContractorModuleEnabled" cols="12" md="2">
        <v-autocomplete
          :items="externalCustomers"
          :label="$t('delegation.form.fields.externalCustomer.name')"
          clearable
          item-text="name"
          item-value="id"
          v-model="criteria.external_customer_id"
          :placeholder="$t('global.all')"
        />
      </v-col>
      <v-col class="px-2" v-if="isReInvoicingForExternalContractorModuleEnabled" cols="12" md="2">
        <v-autocomplete
          :hint="
            typeof projects.find(v => v.value === criteria.project_id) === 'undefined'
              ? ''
              : $t('global.orderNumber') + ': ' + projects.find(v => v.value === criteria.project_id).order_number
          "
          :items="projectsForExternalCustomer"
          :label="$t('delegation.form.fields.project.name')"
          item-text="name"
          item-value="id"
          clearable
          persistent-hint
          v-model="criteria.project_id"
          :placeholder="$t('global.all')"
        />
      </v-col>
    </v-row>
    <v-checkbox :label="$t('report.allByCriteria')" v-model="criteria.allByCriteria" />
    <v-data-table
      :custom-filter="filterOverAllProperty"
      :search="criteria.search"
      :headers="headers"
      :items="filteredDelegations"
      :loading="$store.getters['Ajax/loading'] ? 'accent' : false"
      item-key="id"
      show-select
      v-model="selectedDelegations"
      v-if="criteria.allByCriteria === false"
    >
      <template v-slot:items="props">
        <td>
          <v-checkbox hide-details primary @change="props.select" :value="props.isSelected" />
        </td>
        <td class="text-left">
          {{ props.item.number }}
        </td>
        <td class="text-left">
          {{ props.item.userFullname }}
        </td>
        <td class="text-left">
          {{ $t("global.fromDateShort") }} <b class="text-no-wrap">{{ props.item.start_date | moment("YYYY-MM-DD") }}</b> <br />
          {{ $t("global.toDateShort") }} <b class="text-no-wrap">{{ props.item.end_date | moment("YYYY-MM-DD") }}</b>
        </td>
        <td class="text-left">
          <b class="text-no-wrap">{{ props.item.accepted_at | moment("YYYY-MM-DD") }}</b> <br />
          <span class="smaller-name">{{ props.item.accepted_by_name }}</span>
        </td>
        <td class="text-left">
          <b class="text-no-wrap">{{ props.item.settled_at | moment("YYYY-MM-DD") }}</b> <br />
          <span class="smaller-name">{{ props.item.settled_by_name }}</span>
        </td>
        <td>
          {{ props.item.external_customer_name ? props.item.external_customer_name : "" }} {{ props.item.project_name ? props.item.project_name : "" }}
          {{ props.item.order_number ? props.item.order_number : "" }}
        </td>
        <td>
          <slot name="action" :item="props.item" />
        </td>
      </template>
    </v-data-table>
  </fieldset>
</template>

<script>
import { dateSpanTypes, dateTypeNames, dateTypes } from "../staticData/Report";
import filterOverAllProperty from "../repository/filterOverAllProperty";
import Moment from "moment";
import DatePicker from "./CustomInputComponents/DatePicker";

export default {
  name: "SelectDelegationByCriteriaComponent",
  components: { DatePicker },
  data: () => ({
    dateSpanTypesConst: dateSpanTypes,
    delegations: [],
    externalCustomers: [],
    projects: [],
    users: [],
    organizationStructures: [],
    costCentres: [],
    selectedDelegations: [],
    criteria: {
      report_type: null,
      file_format: null,
      from_date: null,
      to_date: null,
      year: null,
      quarter: null,
      month: null,
      date_span_type: 1,
      search: null,
      date_type: dateTypes.DATE_DELEGATION_SETTLE,
      external_customer_id: null,
      project_id: null,
      user_ids: [],
      organization_structure_ids: [],
      cost_centre_ids: [],
      exchange_rate_date: null,
      allByCriteria: false
    }
    //delegation_ids: []
  }),
  props: {
    filters: {
      required: false,
      default: null
    },
    getDelegationsAddress: {
      required: true
    },
    getFilterDataAddress: {},
    delegation_ids: {
      required: true
    }
  },
  watch: {
    criteria: {
      deep: true,
      handler(val) {
        this.$emit("update:filters", val);
      }
    },
    selectedDelegations(val) {
      let delegations = [];
      for (let delegation of val) {
        delegations.push(delegation.id);
      }
      this.contextDelegationIds = delegations;
    },
    "criteria.external_customer_id"() {
      this.reportForm.project_id = null;
    },
    "criteria.date_span_type"() {
      this.reportForm.year = null;
      this.reportForm.month = null;
      this.reportForm.quarter = null;
      this.reportForm.from_date = null;
      this.reportForm.to_date = null;
    },
    "criteria.year"(val) {
      if (val !== null) {
        this.setDatesIfPossible();
      }
    },
    "criteria.month"(val) {
      if (val !== null) {
        this.setDatesIfPossible();
      }
    },
    "criteria.quarter"(val) {
      if (val !== null) {
        this.setDatesIfPossible();
      }
    }
  },
  computed: {
    contextDelegationIds: {
      get() {
        return this.delegation_ids;
      },
      set(val) {
        this.$emit("update:delegation_ids", val);
      }
    },
    availableYears() {
      let array = [];
      let currentYear = new Date().getFullYear();
      for (let i = 2017; i <= currentYear; i++) {
        array.push(i);
      }
      return array;
    },
    availableMonths() {
      let array = [];
      for (let i = 1; i <= 12; i++) {
        array.push({ text: this.$t("global.months." + i), value: i });
      }
      return array;
    },
    availableQuarters() {
      return [
        { text: this.$t("global.first"), value: 1 },
        { text: this.$t("global.second"), value: 2 },
        { text: this.$t("global.third"), value: 3 },
        { text: this.$t("global.fourth"), value: 4 }
      ];
    },
    dateSpanTypes() {
      return [
        { text: this.$t("global.dateSpanTypeCustom"), value: this.dateSpanTypesConst.DATE_SPAN_TYPE_CUSTOM },
        { text: this.$t("global.dateSpanTypeMonthly"), value: this.dateSpanTypesConst.DATE_SPAN_TYPE_MONTHLY },
        { text: this.$t("global.dateSpanTypeQuarterly"), value: this.dateSpanTypesConst.DATE_SPAN_TYPE_QUARTERLY },
        { text: this.$t("global.dateSpanTypeYearly"), value: this.dateSpanTypesConst.DATE_SPAN_TYPE_YEARLY }
      ];
    },
    dateTypeNames,
    headers() {
      return [
        { text: this.$t("report.chosenDelegationListHeaders.delegationNumber"), value: "number" },
        { text: this.$t("report.chosenDelegationListHeaders.delegated"), value: "userFullname" },
        { text: this.$t("report.chosenDelegationListHeaders.dates"), value: "start_date" },
        { text: this.$t("report.chosenDelegationListHeaders.acceptation"), value: "accepted_at" },
        { text: this.$t("report.chosenDelegationListHeaders.settlement"), value: "settled_at" },
        { text: this.$t("report.chosenDelegationListHeaders.additionalInfo"), sortable: false },
        { text: "", sortable: false, value: "actions" }
      ];
    },
    filteredDelegations() {
      let delegations = this.delegations;
      if (this.criteria.from_date) {
        if (this.criteria.date_type === dateTypes.DATE_DELEGATION_SETTLE) {
          delegations = delegations.filter(v => v.settled_at >= this.criteria.from_date);
        } else if (this.criteria.date_type === dateTypes.DATE_DELEGATION_ACCEPT) {
          delegations = delegations.filter(v => v.accepted_at >= this.criteria.from_date);
        } else if (this.criteria.date_type === dateTypes.DATE_DELEGATION_SENT) {
          delegations = delegations.filter(v => v.send_at >= this.criteria.from_date);
        } else if (this.criteria.date_type === dateTypes.DATE_DELEGATION_TRIP) {
          delegations = delegations.filter(v => v.end_date >= this.criteria.from_date);
        }
      }
      if (this.criteria.to_date) {
        if (this.criteria.date_type === dateTypes.DATE_DELEGATION_SETTLE) {
          delegations = delegations.filter(v => v.settled_at <= this.criteria.to_date);
        } else if (this.criteria.date_type === dateTypes.DATE_DELEGATION_ACCEPT) {
          delegations = delegations.filter(v => v.accepted_at <= this.criteria.to_date);
        } else if (this.criteria.date_type === dateTypes.DATE_DELEGATION_SENT) {
          delegations = delegations.filter(v => v.send_at <= this.criteria.to_date);
        } else if (this.criteria.date_type === dateTypes.DATE_DELEGATION_TRIP) {
          delegations = delegations.filter(v => v.start_date <= this.criteria.to_date);
        }
      }
      if (this.criteria.external_customer_id) {
        delegations = delegations.filter(v => v.external_customer_id === this.criteria.external_customer_id);
      }
      if (this.criteria.project_id) {
        delegations = delegations.filter(v => v.project_id === this.criteria.project_id);
      }
      if (this.criteria.user_ids.length > 0) {
        delegations = delegations.filter(v => this.criteria.user_ids.includes(v.user_id));
      }
      if (this.criteria.cost_centre_ids.length > 0) {
        delegations = delegations.filter(v => this.criteria.cost_centre_ids.includes(v.cost_centre_id));
      }
      if (this.criteria.organization_structure_ids.length > 0) {
        delegations = delegations.filter(v => this.criteria.organization_structure_ids.includes(v.organization_structure_id));
      }
      return delegations;
    },
    isReInvoicingForExternalContractorModuleEnabled() {
      return this.$store.getters["userProperties"].system_settings.external_customers_module === 2;
    },
    projectsForExternalCustomer() {
      return this.projects.filter(v => v.external_customer_id === this.criteria.external_customer_id);
    }
  },
  methods: {
    setDatesIfPossible() {
      // eslint-disable-next-line
      if (this.criteria.date_span_type === this.dateSpanTypesConst.DATE_SPAN_TYPE_YEARLY && !!this.criteria.year) {
        this.criteria.from_date = this.criteria.year + "-01-01";
        this.criteria.to_date = this.criteria.year + "-12-31";
      }
      // eslint-disable-next-line
      if (this.criteria.date_span_type === this.dateSpanTypesConst.DATE_SPAN_TYPE_QUARTERLY && !!this.criteria.year && !!this.criteria.quarter) {
        let moment = new Moment();
        moment.set("year", this.criteria.year);
        moment.set("quarter", this.criteria.quarter);
        this.criteria.from_date = moment.startOf("quarter").format("YYYY-MM-DD");
        this.criteria.to_date = moment.endOf("quarter").format("YYYY-MM-DD");
      }
      // eslint-disable-next-line
      if (this.criteria.date_span_type === this.dateSpanTypesConst.DATE_SPAN_TYPE_MONTHLY && !!this.criteria.year && !!this.criteria.month) {
        let moment = new Moment();
        moment.set("year", this.criteria.year);
        moment.set("month", this.criteria.month - 1);
        this.criteria.from_date = moment.startOf("month").format("YYYY-MM-DD");
        this.criteria.to_date = moment.endOf("month").format("YYYY-MM-DD");
      }
    },
    filterOverAllProperty,
    downloadDelegations() {
      this.axios.get("/report/get-delegations").then(resp => {
        this.delegations = resp.data;
      });
    },
    getFiltersData() {
      this.axios.get("/report/get-filters-data").then(resp => {
        this.projects = resp.data.projects;
        this.externalCustomers = resp.data.external_customers;
        this.users = resp.data.users;
        this.organizationStructures = resp.data.organization_structures;
        this.costCentres = resp.data.cost_centres;
      });
    }
  },
  created() {
    this.downloadDelegations();
    this.getFiltersData();
  }
};
</script>

<style scoped>
.smaller-name {
  font-size: 12px;
}
</style>
